import React, { useState, useEffect, useRef } from "react";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";

// Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import * as XLSX from "xlsx";
import ResizableDataGrid from "components/ResizableDataGrid";

function SingleSeries() {
  const { id } = useParams();
  const auth = localStorage.getItem("authToken");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [values, setValues] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [loading1, setLoading1] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState("all"); // "all", "sold", "unsold"
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  // Refs so Export button uses current dialog values (avoids stale state)
  const exportStatusRef = useRef("");
  const exportFromDateRef = useRef("");
  const exportToDateRef = useRef("");

  useEffect(() => {
    if (id) {
      getUserHistory();
    }
    if (auth) {
      const decodedJwt = JSON.parse(atob(auth.split(".")[1]));
      if (decodedJwt.exp * 1000 < Date.now()) {
        navigate("/sign-in");
      }
    }
  }, [id, auth]);

  const getUserHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASEURL}/admin/product/getcataloguesdata`,
        {
          series_id: id,
        },
        {
          headers: {
            Authorization: `Bearer ${auth}`,
          },
        }
      );
      if (response.data.success) {
        setOrderHistory(response.data.data);
        enqueueSnackbar("Series Fetched successfully", {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Failed", { variant: "error" });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Error occurred. Please try again.", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const openExportDialog = () => {
    setExportStatus("all");
    setExportFromDate("");
    setExportToDate("");
    exportStatusRef.current = "all";
    exportFromDateRef.current = "";
    exportToDateRef.current = "";
    setExportDialogOpen(true);
  };

  const closeExportDialog = () => {
    setExportDialogOpen(false);
  };

  const getRowDate = (row) => {
    const d = row.order_date ?? row.created_at ?? row.sale_date ?? row.date ?? row.updated_at ?? "";
    if (!d) return null;
    if (typeof d === "string") {
      const parsed = d.split("T")[0] || d.split(" ")[0];
      return parsed;
    }
    return null;
  };

  const formatOrderDateForExport = (row) => {
    const d = row.order_date ?? row.created_at ?? row.sale_date ?? row.date ?? row.updated_at;
    if (!d) return "";
    try {
      const dateObj = typeof d === "string" ? new Date(d) : d;
      return format(dateObj, "dd-MM-yyyy");
    } catch {
      return typeof d === "string" ? d.split("T")[0] || d : "";
    }
  };

  const toFilenameDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return "";
    try {
      return format(new Date(yyyyMmDd), "dMMMyyyy").toLowerCase();
    } catch {
      return "";
    }
  };

  // API may return billing id under various keys; check all common names and nested paths
  const getBillingId = (row) => {
    if (!row || typeof row !== "object") return "";
    const flat = [
      row.billing_id,
      row.order_id,
      row.invoice_id,
      row.invoice_number,
      row.invoice_no,
      row.billing_no,
      row.bill_id,
      row.order_billing_id,
      row.billing_number,
    ];
    for (const v of flat) {
      if (v != null && v !== "") return String(v);
    }
    const nested = [
      row.order?.billing_id,
      row.order?.order_id,
      row.order?.invoice_id,
      row.order?.invoice_number,
      row.billing?.billing_id,
      row.billing?.id,
      row.billing?.order_id,
    ];
    for (const v of nested) {
      if (v != null && v !== "") return String(v);
    }
    // Fallback: any key on row that looks like billing/order/invoice
    const key = Object.keys(row).find(
      (k) => /billing_id|order_id|invoice_id|invoice_no|bill_id|billing_no/i.test(k) && row[k] != null && row[k] !== ""
    );
    if (key) return String(row[key]);
    // Deep one level: row.order or row.billing as object
    if (row.order && typeof row.order === "object") {
      const o = row.order;
      const v = o.billing_id ?? o.order_id ?? o.invoice_id ?? o.invoice_number;
      if (v != null && v !== "") return String(v);
    }
    if (row.billing && typeof row.billing === "object") {
      const b = row.billing;
      const v = b.billing_id ?? b.id ?? b.order_id;
      if (v != null && v !== "") return String(v);
    }
    return "";
  };

  const runExportToExcel = () => {
    if (!orderHistory?.length) {
      enqueueSnackbar("No data to export", { variant: "info" });
      return;
    }
    // Use refs so we get the values currently in the dialog (fixes stale state on Export click)
    const statusFilter = (exportStatusRef.current ?? exportStatus).toString().trim().toLowerCase();
    const fromDate = (exportFromDateRef.current ?? exportFromDate).toString().trim();
    const toDate = (exportToDateRef.current ?? exportToDate).toString().trim();

    let filtered = [...orderHistory];
    // Filter by status (sold / unsold) – "all" = no filter. API: status "sold" | "unsold", billing_status "store sales" | "delivered" | null
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((row) => {
        const rowStatus = (row.status ?? "").toString().toLowerCase().trim();
        const billingStatus = (row.billing_status ?? "").toString().toLowerCase().trim();
        const hasBillingId = row.billing_id != null && row.billing_id !== "";
        if (statusFilter === "sold") {
          // Exclude unsold: status "unsold" with no billing means not sold
          if (rowStatus === "unsold" && !billingStatus && !hasBillingId) return false;
          return (
            rowStatus === "sold" ||
            billingStatus === "store sales" ||
            billingStatus === "delivered" ||
            hasBillingId
          );
        }
        if (statusFilter === "unsold") {
          return (
            rowStatus === "unsold" ||
            ((!rowStatus || rowStatus === "unsold") && !billingStatus && !hasBillingId)
          );
        }
        return rowStatus === statusFilter || billingStatus === statusFilter;
      });
    }
    // Filter by date range only when Sold
    // 2.1 no date → all sold; 2.2 From only → from that date up to today; 2.3 To only → start to toDate; 2.4 both → that interval
    if (statusFilter === "sold" && (fromDate || toDate)) {
      const effectiveTo = toDate || format(new Date(), "yyyy-MM-dd");
      const effectiveFrom = fromDate || "";
      filtered = filtered.filter((row) => {
        const rowDate = getRowDate(row);
        if (!rowDate) return false;
        if (effectiveFrom && rowDate < effectiveFrom) return false;
        if (effectiveTo && rowDate > effectiveTo) return false;
        return true;
      });
    }
    if (!filtered.length) {
      enqueueSnackbar("No records match the selected filters", { variant: "info" });
      return;
    }
    // Sort: Sold → by order_date latest first; Unsold → by catalogue_id
    if (statusFilter === "sold") {
      filtered.sort((a, b) => {
        const dateA = a.order_date ? new Date(a.order_date).getTime() : 0;
        const dateB = b.order_date ? new Date(b.order_date).getTime() : 0;
        return dateA - dateB;
      });
    } else if (statusFilter === "unsold") {
      filtered.sort((a, b) => {
        const idA = (a.catalogue_id ?? "").toString();
        const idB = (b.catalogue_id ?? "").toString();
        return idA.localeCompare(idB, undefined, { numeric: true });
      });
    }
    // All three options (All, Sold, Unsold) export with all columns including Order Date
    const rows = filtered.map((row) => ({
      Title: row.title ?? "",
      "Catalogue Id": row.catalogue_id ?? "",
      "Billing Id": row.billing_id != null && row.billing_id !== "" ? String(row.billing_id) : getBillingId(row),
      "Order Date": formatOrderDateForExport(row),
      Status: row.status ?? "",
      "Sale Status": row.billing_status ?? "",
    }));
    const seriesLabel = (id || "catalogue").toUpperCase();
    const todayStr = toFilenameDate(format(new Date(), "yyyy-MM-dd"));
    let fileName;
    if (statusFilter === "sold") {
      const effectiveFrom = fromDate || "";
      const effectiveTo = toDate || format(new Date(), "yyyy-MM-dd");
      if (effectiveFrom && effectiveTo) {
        fileName = `${seriesLabel}-Sold-${toFilenameDate(effectiveFrom)}-${toFilenameDate(effectiveTo)}.xlsx`;
      } else {
        fileName = `${seriesLabel}-Sold-${todayStr}.xlsx`;
      }
    } else if (statusFilter === "unsold") {
      fileName = `${seriesLabel}-Unsold-${todayStr}.xlsx`;
    } else {
      fileName = `${seriesLabel}-All-${todayStr}.xlsx`;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, id ? `Series ${id}` : "Catalogue");
    XLSX.writeFile(wb, fileName);
    enqueueSnackbar("Excel exported successfully", { variant: "success" });
    closeExportDialog();
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      width: 200,
    },
    {
      field: "catalogue_id",
      headerName: "Catalogue Id",
      width: 200,
      valueGetter: (params) => params.row?.catalogue_id,
    },
    {
      field: "billing_id",
      headerName: "Billing Id",
      width: 200,
      valueGetter: (params) => {
        const r = params.row ?? {};
        return r.billing_id != null && r.billing_id !== "" ? String(r.billing_id) : getBillingId(r);
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
    },
    {
      field: "billing_status",
      headerName: "Sale Status",
      width: 130,
    },
    {
      field: "order_date",
      headerName: "Order Date",
      width: 150,
      valueGetter: (params) => formatOrderDateForExport(params.row ?? {}),
    },
    // {
    //   field: "onclick",
    //   headerName: "Action",
    //   width: 200,
    //   renderCell: (params) => (
    //     <strong>
    //       <MDButton
    //         variant="contained"
    //         size="small"
    //         onClick={() => {
    //           navigate(`/orders/${params.row.order_id}`);
    //         }}
    //       >
    //         Open
    //       </MDButton>
    //     </strong>
    //   ),
    // },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar hidebreadcrumbTitle />
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <MDBox pt={3} pb={3}>
            <Grid container spacing={6}>
              {/* {values && ( */}
                <Grid item xs={12}>
                  <Card>
                    <MDBox
                      mx={2}
                      mt={-3}
                      py={3}
                      px={2}
                      variant="gradient"
                      bgColor="info"
                      borderRadius="lg"
                      coloredShadow="info"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" color="white">
                        {id}
                      </MDTypography>
                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={openExportDialog}
                        disabled={!orderHistory?.length}
                      >
                        Export to Excel
                      </MDButton>
                    </MDBox>

                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Card variant="outlined">
                          <MDBox pt={3}>
                            {loading1 ? (
                              <CircularProgress
                                style={{
                                  marginTop: "80px",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  height: "100vh",
                                }}
                                color="inherit"
                              />
                            ) : (
                              <>
                                {orderHistory && (
                                  <MDBox sx={{ height: 400 }} p={2}>
                                    <ResizableDataGrid
                                      getRowId={(row) => row.catalogue_id}
                                      rows={orderHistory}
                                      columns={columns}
                                      initialState={{
                                        pagination: {
                                          paginationModel: {
                                            pageSize: 100,
                                          },
                                        },
                                      }}
                                      // pageSizeOptions={[5, 10, 20, 50]}
                                      disableRowSelectionOnClick
                                    />
                                  </MDBox>
                                )}
                              </>
                            )}
                          </MDBox>
                        </Card>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              {/* )} */}
            </Grid>
          </MDBox>
        </>
      )}

      <Dialog open={exportDialogOpen} onClose={closeExportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Export to Excel</DialogTitle>
        <DialogContent>
          <MDBox pt={1} pb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { minHeight: 40 } }}>
                  <InputLabel id="export-status-label">Status</InputLabel>
                  <Select
                    labelId="export-status-label"
                    value={exportStatus}
                    label="Status"
                    onChange={(e) => {
                      const v = e.target.value;
                      setExportStatus(v);
                      exportStatusRef.current = v;
                    }}
                    sx={{ minHeight: 40 }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                    <MenuItem value="unsold">Unsold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {(exportStatus === "sold") && (
                <>
                  <Grid item xs={12} sm={6}>
                    <MDInput
                      type="date"
                      label="From date"
                      value={exportFromDate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setExportFromDate(v);
                        exportFromDateRef.current = v;
                      }}
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <MDInput
                      type="date"
                      label="To date"
                      value={exportToDate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setExportToDate(v);
                        exportToDateRef.current = v;
                      }}
                      fullWidth
                      margin="dense"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MDTypography variant="caption" color="text" display="block" sx={{ mt: 0 }}>
                      Leave both dates empty to export all sold data.
                    </MDTypography>
                  </Grid>
                </>
              )}
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="outlined" onClick={closeExportDialog}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={runExportToExcel}>
            Export
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default SingleSeries;